import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSubscription } from '@/contexts/subscription-context'
import { isAuthenticated } from '@/lib/utils'
import { Loader2 } from 'lucide-react'

/**
 * SubscriptionGuard - Protects routes that require active subscription
 * 
 * Usage:
 * <SubscriptionGuard>
 *   <ProtectedComponent />
 * </SubscriptionGuard>
 */
export function SubscriptionGuard({ children, fallback = null }) {
  const { subscription, loading, checked, hasActiveSubscription } = useSubscription()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!checked || loading) return

    // Not authenticated -> redirect to login
    if (!isAuthenticated()) {
      localStorage.setItem('ldirect', location.pathname)
      navigate('/login')
      return
    }

    // Authenticated but no subscription -> redirect to pricing
    if (!hasActiveSubscription()) {
      localStorage.setItem('ldirect', location.pathname)
      navigate('/pricing')
      return
    }
  }, [checked, loading, subscription, navigate, location])

  // Show loading state
  if (loading || !checked) {
    return fallback || (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Not authenticated or no subscription (will redirect in useEffect)
  if (!isAuthenticated() || !hasActiveSubscription()) {
    return null
  }

  // Has active subscription - render children
  return <>{children}</>
}

/**
 * SmartRedirect - Redirects user based on auth and subscription status
 * 
 * Usage in HomePage or landing pages:
 * <SmartRedirect />
 */
export function SmartRedirect() {
  const { subscription, loading, checked, hasActiveSubscription, getCachedSubscription } = useSubscription()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    // Don't redirect if still loading
    if (loading || !checked) return

    // Check if we're already on a specific page (don't redirect unnecessarily)
    const currentPath = location.pathname
    const noRedirectPaths = ['/login', '/signup', '/pricing', '/email/verify', '/faq', '/terms', '/privacy']
    if (noRedirectPaths.includes(currentPath)) {
      return
    }

    // Define public paths that don't require authentication
    const publicPaths = ['/', '/posts', '/provider', '/tags', '/trending', '/search', '/today']
    const isPublicPath = publicPaths.some(path => currentPath === '/' || currentPath.startsWith(path))

    // Not authenticated -> allow public paths, redirect protected routes to login
    if (!isAuthenticated()) {
      // Allow access to public paths (including state news articles)
      if (isPublicPath) return
      
      // Otherwise redirect to login for protected routes
      localStorage.setItem('ldirect', currentPath)
      navigate('/login')
      return
    }

    // Authenticated - check subscription status
    // Only redirect based on subscription if we successfully checked it
    // This prevents redirect loops when token is expired but subscription check hasn't completed
    if (checked) {
      const hasSubscription = hasActiveSubscription()
      
      if (!hasSubscription) {
        // No subscription -> allow browsing public pages, redirect protected routes to pricing
        if (!isPublicPath) {
          localStorage.setItem('ldirect', currentPath)
          navigate('/pricing')
        }
      } else {
        // Has subscription - check if there's a saved redirect
        const savedRedirect = localStorage.getItem('ldirect')
        if (savedRedirect && savedRedirect !== currentPath) {
          localStorage.removeItem('ldirect')
          navigate(savedRedirect)
        }
      }
    }
  }, [loading, checked, subscription, navigate, location, hasActiveSubscription])

  return null
}

/**
 * RequireAuth - Simple auth guard (doesn't check subscription)
 */
export function RequireAuth({ children, fallback = null }) {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!isAuthenticated()) {
      localStorage.setItem('ldirect', location.pathname)
      navigate('/login')
    }
  }, [navigate, location])

  if (!isAuthenticated()) {
    return fallback || null
  }

  return <>{children}</>
}
